import { FC, Fragment, useState, useMemo, useContext, useRef } from 'react';
import { Table } from '@/components/table';
import { Button } from '@/components/ui/button';
import { ColumnDef } from '@tanstack/react-table';
import {
  MoreHorizontal,
  MoreVertical,
  Clock,
  Edit,
  Play,
  CopyIcon,
  Trash2,
  Plus,
  FileText,
  CopyPlus,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  Share2,
  Lock,
  Unlock,
  CalendarClock,
  Bot,
  Copy,
  ClipboardCheck,
  ClipboardList,
} from 'lucide-react';
import { useOrgNavigate } from '@/hooks/use-org-navigate';
import { useNotification } from '@/components/notifications';
import {
  Workflow,
  WorkflowDetails,
  WorkflowSortField,
} from '@/types/workflow-types';
import { WorkflowCard } from '@/components/workflow/workflow-card';
import { hasClientDataLabel, isSharedPublicly } from '@/utils/workflow-summary';
import { formatRunCost } from '@/utils/format-cost';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useWorkflowStore } from '@/stores/workflow-store';
import { SaveWorkflowModal } from './save-workflow-modal';
import { useOrgStore } from '@/stores/org-store';
import { DeleteConfirmationModal } from '@/components/delete-confirmation-modal';
import { ShareModal } from '@/components/share-modal';
import { CreateOrchestratorModal } from './create-orchestrator-modal';
import { ApplyToUatModal } from './apply-to-uat-modal';
import { SignOffModal } from './sign-off-modal';
import { useOrgQualificationContext } from '@/hooks/queries/use-org-qualification-context';
import { ROUTES } from '@/constants/routes';
import { orgPath } from '@/utils/org-path';
import { RUN_ID_PARAM } from '@/constants/workflow-editor';
import { getWorkflowInputs } from '@/services/workflow/get-workflow-inputs';
import { asyncRunWorkflowVersion } from '@/services/workflow/run-workflow-version';
import { getRunSession } from '@/services/workflow/get-run-session';
import { TruncateTextWithTooltip } from '@/components/ui/truncate-text-with-tooltip';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '@/components/ui/tooltip';
import { Pagination } from '@/components/pagination';
import { formatTimeAgo, safeFormatDate } from '@/utils/format-date';
import { AuthContext } from '@/context/auth-context';
import {
  isAccessDeniedError,
  ACCESS_REVOKED_MESSAGE,
} from '@/pages/workflow/helper/is-access-denied-error';
import { useLabelPicker, LabelBadges } from '@/components/label-picker';
import { PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { Modal } from '@/components/modal';
import { ScheduleConfigSection } from './controls/schedule-sidebar';
import { parseCronToMode, humanCron } from './controls/schedule-helpers';
import { useLaunchDarkly } from '@/providers/launchdarkly-provider';
import { requestWorkflowAccess } from '@/services/workflow/request-workflow-access';
import { setWorkflowDefaultRole } from '@/services/workflow/workflow-default-role';
import { lockWorkflow } from '@/services/workflow/lock-workflow';
import { getWorkflow } from '@/services/workflow/get-workflow';
import { updateWorkflow } from '@/services/workflow/update-workflow';
import * as Sentry from '@sentry/react';
import type { DefaultRoleOption } from '@/types/share-types';

const ITEMS_PER_PAGE = 10;

// Mirror the editor's required-field validation: null/undefined, blank or
// whitespace-only strings, and empty collections/objects all count as "not
// provided", so the list Run gate blocks exactly what the editor blocks.
function isBlankInputValue(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
}

function formatTimeUntil(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = date.getTime() - now.getTime();
  if (diffMs < 0) return 'overdue';
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'in <1 min';
  if (diffMin < 60) return `in ${diffMin} min`;
  const diffHrs = Math.floor(diffMin / 60);
  if (diffHrs < 24) return `in ${diffHrs}h`;
  const diffDays = Math.floor(diffHrs / 24);
  return `in ${diffDays}d`;
}

interface WorkflowListTableProps {
  isLoading: boolean;
  viewMode: 'grid' | 'list';
  openCreateModal: () => void;
  handleSetCurrentPage: (page: number) => void;
  sortField?: WorkflowSortField;
  sortDirection?: 'asc' | 'desc';
  onSort?: (field: WorkflowSortField) => void;
  searchQuery?: string;
  currentPage?: number;
  selectedLabels?: string[];
  showMyWorkflows?: boolean;
  showSharedWithMe?: boolean;
}

// Empty state component
const EmptyState: FC<{ openCreateModal: () => void }> = ({
  openCreateModal,
}) => {
  return (
    <div className='flex flex-col items-center justify-center py-16 px-4 min-h-[400px] w-full'>
      <div className='w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-6'>
        <FileText className='w-12 h-12 text-gray-400' />
      </div>
      <h3 className='text-lg font-semibold text-gray-900 mb-2 text-center dark:text-white'>
        No workflows yet
      </h3>
      <p className='text-gray-500 text-center mb-6 max-w-md'>
        Create your first workflow to get started with building and automating
        your processes.
      </p>
      <Button onClick={() => openCreateModal()} className='gap-2'>
        <Plus className='w-4 h-4' />
        Create Workflow
      </Button>
    </div>
  );
};

const WorkflowTitleCell: FC<{
  workflow: WorkflowDetails;
  orgId: string | undefined;
  isLabelsEnabled: boolean;
  canEditLabels: boolean;
  viewerOnly: boolean;
}> = ({ workflow, orgId, isLabelsEnabled, canEditLabels, viewerOnly }) => {
  const labelIds = useMemo(
    () => (workflow.labels ?? []).map((l) => l.id),
    [workflow.labels],
  );
  const picker = useLabelPicker({
    workflowId: workflow.workflow_id,
    initialLabelIds: labelIds,
    orgId,
    stopPropagation: true,
    modal: true,
  });

  return (
    <div className={cn('flex items-start gap-3', viewerOnly && 'opacity-50')}>
      <div className='flex flex-col flex-1 gap-2 min-w-0'>
        <div className='flex items-center gap-1.5 min-w-0'>
          <span
            className='font-semibold text-sm truncate leading-5 min-w-0'
            title={workflow.name}
          >
            {workflow.name}
          </span>
          {workflow.is_locked && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Lock className='size-3.5 shrink-0 text-amber-500' />
              </TooltipTrigger>
              <TooltipContent>
                <p>This workflow is locked</p>
              </TooltipContent>
            </Tooltip>
          )}
          {isLabelsEnabled &&
            canEditLabels &&
            picker.renderPopover(
              <PopoverTrigger asChild>
                <button
                  type='button'
                  aria-label='Add label'
                  onClick={(e) => e.stopPropagation()}
                  className='ml-auto shrink-0 inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs text-muted-foreground bg-muted border border-border hover:bg-accent hover:text-foreground transition-colors cursor-pointer opacity-0 group-hover/row:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100'
                >
                  <Plus className='size-3' />
                  Add Label
                </button>
              </PopoverTrigger>,
            )}
        </div>
        {workflow.description && (
          <TruncateTextWithTooltip
            text={workflow.description}
            interpretPlainTextFormat
            maxLines={2}
            className='text-sm text-muted-foreground leading-snug'
          />
        )}
        {isLabelsEnabled &&
          canEditLabels &&
          picker.displayLabels.length > 0 && (
            <LabelBadges
              labels={picker.displayLabels}
              onRemove={picker.toggleLabel}
              maxVisible={Infinity}
              onContainerClick={(e) => e.stopPropagation()}
            />
          )}
        {isLabelsEnabled && !canEditLabels && labelIds.length > 0 && (
          <LabelBadges labels={workflow.labels} maxVisible={Infinity} />
        )}
      </div>
      {picker.createModalEl}
    </div>
  );
};

export const WorkflowListTable: FC<WorkflowListTableProps> = ({
  isLoading,
  viewMode,
  openCreateModal,
  sortField = 'updated_at',
  sortDirection = 'desc',
  onSort,
  searchQuery = '',
  currentPage = 1,
  handleSetCurrentPage,
  selectedLabels = [],
  showMyWorkflows = false,
  showSharedWithMe = false,
}) => {
  const navigate = useOrgNavigate();
  const notification = useNotification();
  const { user } = useContext(AuthContext);
  const {
    savedWorkflows,
    setSavedWorkflows,
    deleteWorkflow,
    updateWorkflowMetadata,
    duplicateWorkflow,
    getWorkflowList,
  } = useWorkflowStore();
  const { currentOrg } = useOrgStore();
  const {
    isLabelsEnabled,
    isShareClientDataWarningEnabled,
    isWorkflowLockingEnabled,
    isWorkflowSchedulingEnabled,
    isWorkflowRunCostEnabled,
    isCreatorEmailTruncated,
    isQualificationEnabled,
  } = useLaunchDarkly();
  const { isWorkflowAdmin } = useWorkflowStore();

  // One call per ORG, not per row: the rung an org holds is what decides
  // which action every workflow in it offers. Disabled entirely when the
  // flag is off, so a non-EY org issues no request at all.
  const { data: qualificationContext } = useOrgQualificationContext(
    currentOrg?.orgid,
    { enabled: isQualificationEnabled && !!currentOrg?.orgid },
  );
  const qualificationStages = qualificationContext?.stages ?? [];

  // State for edit workflow modal
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingWorkflow, setEditingWorkflow] =
    useState<WorkflowDetails | null>(null);
  // Full workflow payload (nodes + edges + flags) loaded lazily when the
  // Edit Workflow Attributes modal opens. We need the whole payload to
  // re-POST through ``updateWorkflow`` when the user flips
  // ``publish_to_api`` -- same contract the canvas uses, just sourced
  // from a list-local fetch instead of the workflow store.
  const [editLoadedWorkflow, setEditLoadedWorkflow] = useState<Workflow | null>(
    null,
  );

  // State for delete confirmation modal
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [workflowToDelete, setWorkflowToDelete] =
    useState<WorkflowDetails | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);

  // State for share modal
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [sharingWorkflowId, setSharingWorkflowId] = useState<string | null>(
    null,
  );

  // State for "Create orchestrator" modal (one-click A2A orchestrator
  // agent creation, opened from the kebab menu).
  const [isOrchestratorModalOpen, setIsOrchestratorModalOpen] = useState(false);
  const [orchestratorWorkflow, setOrchestratorWorkflow] =
    useState<WorkflowDetails | null>(null);

  const [isApplyUatModalOpen, setIsApplyUatModalOpen] = useState(false);
  const [applyUatWorkflow, setApplyUatWorkflow] =
    useState<WorkflowDetails | null>(null);
  const [isSignOffModalOpen, setIsSignOffModalOpen] = useState(false);
  const [signOffWorkflow, setSignOffWorkflow] =
    useState<WorkflowDetails | null>(null);

  // State for schedule modal
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [schedulingWorkflowId, setSchedulingWorkflowId] = useState<
    string | null
  >(null);
  const [schedulingWorkflowName, setSchedulingWorkflowName] = useState('');
  const scheduleChangedRef = useRef(false);

  // Track workflows where user just requested access (optimistic)
  const [pendingRequests, setPendingRequests] = useState<Set<string>>(
    new Set(),
  );

  // Pagination state
  // Define workflow actions menu items
  interface WorkflowAction {
    label: string;
    icon: React.ReactNode;
    onSelect: () => void;
    className?: string;
    badge?: number;
    disabled?: boolean;
  }

  const handleRequestAccess = async (workflowId: string) => {
    if (!currentOrg?.orgid) return;
    try {
      await requestWorkflowAccess(currentOrg.orgid, workflowId);
      setPendingRequests((prev) => new Set(prev).add(workflowId));
      notification.success('Access request sent');
    } catch {
      notification.error('Failed to send access request');
    }
  };

  const hasPendingRequest = (workflow: WorkflowDetails): boolean =>
    !!workflow.has_pending_request || pendingRequests.has(workflow.workflow_id);

  const handleToggleLock = async (workflow: WorkflowDetails) => {
    if (!currentOrg?.orgid) return;
    const newLockedState = !workflow.is_locked;
    if (!newLockedState && !isWorkflowAdmin) return;

    setSavedWorkflows(
      savedWorkflows.map((w) =>
        w.workflow_id === workflow.workflow_id
          ? { ...w, is_locked: newLockedState }
          : w,
      ),
    );

    try {
      await lockWorkflow(
        currentOrg.orgid,
        workflow.workflow_id,
        newLockedState,
      );
      notification.success(
        newLockedState ? 'Workflow locked' : 'Workflow unlocked',
      );
    } catch (error) {
      setSavedWorkflows(
        savedWorkflows.map((w) =>
          w.workflow_id === workflow.workflow_id
            ? { ...w, is_locked: workflow.is_locked }
            : w,
        ),
      );
      notification.error(
        newLockedState
          ? 'Failed to lock workflow'
          : 'Failed to unlock workflow',
      );
      Sentry.captureException(error, {
        extra: {
          context: 'workflow-list-toggle-lock',
          workflowId: workflow.workflow_id,
        },
      });
    }
  };

  const isViewerOnly = (workflow: WorkflowDetails): boolean => {
    // Workflow admins (users in the ``workflow_admin`` table) outrank
    // any per-workflow role -- they always get the full action menu so
    // the canvas and the list-row dropdown agree on visibility.
    if (isWorkflowAdmin) return false;
    const role = workflow.user_role;
    const isOwner =
      role === 'workflow:owner' ||
      (user?.email &&
        workflow.created_by_email?.toLowerCase() === user.email?.toLowerCase());
    return !isOwner && (!role || role === 'workflow:viewer');
  };

  const getWorkflowActions = (workflow: WorkflowDetails): WorkflowAction[] => {
    const role = workflow.user_role;
    const isOwner =
      role === 'workflow:owner' ||
      (user?.email &&
        workflow.created_by_email?.toLowerCase() === user.email?.toLowerCase());
    // Workflow admins (``is_workflow_admin`` from the v1 list endpoint, see
    // ``WorkflowAccess.is_workflow_admin``) always get full edit access -- the
    // backend bypasses ``require_workflow_permission`` for them, so any
    // edit/share/schedule/lock action they trigger from the list dropdown is
    // accepted server-side regardless of their per-row workflow_access role.
    const canEdit = isOwner || role === 'workflow:editor' || isWorkflowAdmin;

    // Editor / Owner — full actions
    const actions: WorkflowAction[] = [
      {
        label: 'Edit Workflow',
        icon: <Edit className='size-4' />,
        onSelect: () =>
          navigate(`${ROUTES.WORKFLOW.ROOT}/${workflow.workflow_id}`),
      },
    ];

    if (canEdit) {
      actions.push({
        label: 'Run',
        icon: <Play className='size-4' />,
        onSelect: () => handleRunWorkflow(workflow),
      });
    }

    if (isOwner || isWorkflowAdmin) {
      actions.push({
        label: 'Edit Workflow Attributes',
        icon: <Edit className='size-4' />,
        onSelect: () => editWorkflowDetails(workflow.workflow_id),
        disabled: !!workflow.is_locked,
      });
    }

    if (canEdit) {
      const pendingCount = workflow.pending_request_count ?? 0;
      actions.push({
        label: pendingCount > 0 ? `Share · ${pendingCount} pending` : 'Share',
        icon: <Share2 className='size-4' />,
        badge: pendingCount > 0 ? pendingCount : undefined,
        onSelect: () => {
          setSharingWorkflowId(workflow.workflow_id);
          setIsShareModalOpen(true);
        },
      });
    }

    if (canEdit) {
      actions.push({
        label: 'Create orchestrator',
        icon: <Bot className='size-4' />,
        onSelect: () => {
          setOrchestratorWorkflow(workflow);
          setIsOrchestratorModalOpen(true);
        },
      });
    }

    actions.push(
      {
        label: 'Duplicate',
        icon: <CopyPlus className='size-4' />,
        onSelect: () =>
          handleDuplicateWorkflow(
            workflow.workflow_id,
            workflow.name + ' (Copy)',
          ),
      },
      {
        label: 'Copy ID',
        icon: <CopyIcon className='size-4' />,
        onSelect: async () => {
          try {
            await navigator.clipboard.writeText(workflow.workflow_id);
            notification.success('Copied to clipboard');
          } catch (err) {
            console.error('Failed to copy:', err);
            notification.error('Failed to copy to clipboard');
          }
        },
      },
    );

    if (isWorkflowSchedulingEnabled && canEdit) {
      actions.push({
        label: 'Schedule',
        icon: <CalendarClock className='size-4' />,
        onSelect: () => {
          setSchedulingWorkflowId(workflow.workflow_id);
          setSchedulingWorkflowName(workflow.name ?? '');
          setIsScheduleModalOpen(true);
        },
      });
    }

    if (isWorkflowLockingEnabled && canEdit) {
      if (workflow.is_locked) {
        if (isWorkflowAdmin) {
          actions.push({
            label: 'Unlock Workflow',
            icon: <Unlock className='size-4' />,
            onSelect: () => handleToggleLock(workflow),
          });
        } else {
          actions.push({
            label: 'Locked',
            icon: <Lock className='size-4' />,
            onSelect: () => {},
            disabled: true,
          });
        }
      } else {
        actions.push({
          label: 'Lock Workflow',
          icon: <Lock className='size-4' />,
          onSelect: () => handleToggleLock(workflow),
        });
      }
    }

    // Which action this org offers depends on the rung it holds. Apply in
    // a build org, sign-off in pre-UAT, nothing in UAT: a copy there is
    // locked and pinned, so there is nothing to do to it.
    //
    // Whether a wave is open, and whether the entry conditions pass, is
    // the apply modal's business: greying the item here would hide the
    // reason.
    if (
      isQualificationEnabled &&
      qualificationStages.includes('build') &&
      canEdit
    ) {
      actions.push({
        label: 'Apply to UAT',
        icon: <ClipboardCheck className='size-4' />,
        onSelect: () => {
          setApplyUatWorkflow(workflow);
          setIsApplyUatModalOpen(true);
        },
        disabled: !!workflow.is_locked,
      });
    }

    // View, not edit: an approver signing off is not editing, and a client
    // reviewer may hold only view on the copy.
    if (isQualificationEnabled && qualificationStages.includes('pre_uat')) {
      actions.push({
        label: 'Sign-off status',
        icon: <ClipboardList className='size-4' />,
        onSelect: () => {
          setSignOffWorkflow(workflow);
          setIsSignOffModalOpen(true);
        },
      });
    }

    if (isOwner || isWorkflowAdmin) {
      actions.push({
        label: 'Delete',
        icon: <Trash2 className='size-4' />,
        onSelect: () => handleDeleteWorkflow(workflow.workflow_id),
        disabled: !!workflow.is_locked,
      });
    }

    return actions;
  };

  const editWorkflowDetails = async (workflowId: string) => {
    const workflow = savedWorkflows.find((w) => w.workflow_id === workflowId);
    if (!workflow) return;
    setEditingWorkflow(workflow);
    setEditLoadedWorkflow(null);
    setIsEditModalOpen(true);
    // Lazily fetch the full payload so the modal's ``Expose via A2A API``
    // toggle has something to read + something to POST back. If this fails
    // we keep the modal open in degraded mode -- name/description edit
    // still works via the lightweight ``updateWorkflowMetadata`` path.
    if (!currentOrg?.orgid) return;
    try {
      const resp = await getWorkflow(workflowId, currentOrg.orgid);
      if (resp?.data?.workflow?.payload) {
        setEditLoadedWorkflow(resp.data.workflow.payload as Workflow);
      }
    } catch (err) {
      Sentry.captureException(err, {
        extra: { context: 'workflow-list-edit-load', workflowId },
      });
    }
  };

  const handleDeleteWorkflow = (workflowId: string) => {
    const workflow = savedWorkflows.find((w) => w.workflow_id === workflowId);
    if (workflow) {
      setWorkflowToDelete(workflow);
      setIsDeleteModalOpen(true);
    }
  };

  const confirmDeleteWorkflow = async () => {
    if (!workflowToDelete || !currentOrg?.orgid) {
      return;
    }

    setIsDeleting(true);
    try {
      await deleteWorkflow(workflowToDelete.workflow_id, currentOrg.orgid);
      // Refresh the workflow list
      getWorkflowList(currentOrg.orgid);
      notification.success('Workflow deleted successfully');
    } catch (error) {
      console.error('Error deleting workflow:', error);
      const errorMessage = error instanceof Error ? error.message : undefined;
      if (errorMessage && isAccessDeniedError(errorMessage)) {
        notification.error(ACCESS_REVOKED_MESSAGE);
        getWorkflowList(currentOrg.orgid);
      } else {
        notification.error(
          errorMessage || 'Failed to delete workflow. Please try again.',
        );
      }
    } finally {
      setIsDeleting(false);
      setIsDeleteModalOpen(false);
      setWorkflowToDelete(null);
    }
  };

  const cancelDeleteWorkflow = () => {
    setIsDeleteModalOpen(false);
    setWorkflowToDelete(null);
  };

  const handleUpdateWorkflow = async (
    newName: string,
    newDescription?: string,
  ) => {
    if (!editingWorkflow || !currentOrg?.orgid) {
      return;
    }

    try {
      await updateWorkflowMetadata(
        editingWorkflow.workflow_id,
        newName,
        newDescription,
        currentOrg.orgid,
        false, // isTemplate = false for workflows
      );
      getWorkflowList(currentOrg.orgid);
      notification.success('Workflow updated successfully');
      setIsEditModalOpen(false);
      setEditingWorkflow(null);
    } catch (error) {
      console.error('Error updating workflow:', error);
      throw error; // Re-throw to let the modal handle the error display
    }
  };

  const handleDuplicateWorkflow = async (
    workflowId: string,
    dupName: string,
  ) => {
    if (!currentOrg?.orgid) {
      notification.error("You don't belong to an organization");
      return;
    }

    setIsDuplicating(true);
    try {
      await duplicateWorkflow(workflowId, currentOrg.orgid, dupName);
      // Refresh the workflow list to show the new duplicate
      await getWorkflowList(currentOrg.orgid);
      notification.success('Workflow duplicated successfully');
    } catch (error) {
      console.error('Error duplicating workflow:', error);
      const errorMessage = error instanceof Error ? error.message : undefined;
      if (errorMessage && isAccessDeniedError(errorMessage)) {
        notification.error(ACCESS_REVOKED_MESSAGE);
        getWorkflowList(currentOrg.orgid);
      } else {
        notification.error(
          errorMessage || 'Failed to duplicate workflow. Please try again.',
        );
      }
    } finally {
      setIsDuplicating(false);
    }
  };

  const handleRunWorkflow = async (workflow: WorkflowDetails) => {
    if (!currentOrg?.orgid) {
      notification.error("You don't belong to an organization");
      return;
    }

    const orgId = currentOrg.orgid;
    const workflowId = workflow.workflow_id;
    try {
      const { version_id, inputs } = await getWorkflowInputs(orgId, workflowId);
      const missingRequired = inputs.filter(
        (field) => field.required && isBlankInputValue(field.current_value),
      );

      if (missingRequired.length > 0) {
        const plural = missingRequired.length > 1;
        notification.warning(
          `"${workflow.name}" has ${missingRequired.length} required input${plural ? 's' : ''}. Open the workflow to provide ${plural ? 'them' : 'it'} before running.`,
        );
        return;
      }

      const session = await getRunSession();
      if (!session?.session_id) {
        throw new Error('Failed to create a run session');
      }

      const { run_id } = await asyncRunWorkflowVersion(orgId, workflowId, {
        session_id: session.session_id,
        version_id,
        metadata: { source: 'workflow_list' },
      });

      notification.success(
        <span>
          {`Run started for "${workflow.name}". `}
          <button
            type='button'
            className='underline font-medium'
            onClick={() =>
              navigate(
                `${ROUTES.WORKFLOW.ROOT}/${workflowId}?${RUN_ID_PARAM}=${run_id}`,
              )
            }
          >
            View run
          </button>
        </span>,
        { duration: 15000 },
      );
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : undefined;
      if (errorMessage && isAccessDeniedError(errorMessage)) {
        notification.error(ACCESS_REVOKED_MESSAGE);
      } else {
        notification.error(
          errorMessage || 'Failed to start workflow run. Please try again.',
        );
      }
      Sentry.captureException(error, {
        extra: { context: 'run-workflow-from-list', workflowId },
      });
    }
  };

  // Get filtered and sorted workflows
  const filteredAndSortedWorkflows = useMemo(() => {
    // First filter by search query, labels, and owner/shared filters
    const filteredWorkflows = savedWorkflows.filter((workflow) => {
      // "My workflows" and "Shared with me" act as additive (OR) when both
      // are active: show workflows that match either condition. When only one
      // is active it acts as a standalone filter.
      if ((showMyWorkflows || showSharedWithMe) && user?.email) {
        const isCreator =
          workflow.created_by_email?.toLowerCase() === user.email.toLowerCase();
        const hasAccess = workflow.user_role === 'workflow:editor';

        const matchesMine = showMyWorkflows && isCreator;
        const matchesShared = showSharedWithMe && !isCreator && hasAccess;

        if (!matchesMine && !matchesShared) {
          return false;
        }
      }

      // Label filter
      if (selectedLabels.length > 0) {
        const hasMatchingLabel = selectedLabels.some((label) =>
          workflow.labels?.some((wl) => wl.name === label),
        );
        if (!hasMatchingLabel) return false;
      }

      if (!searchQuery.trim()) return true;

      const query = searchQuery.toLowerCase();
      return (
        workflow.name.toLowerCase().includes(query) ||
        (workflow.description &&
          workflow.description.toLowerCase().includes(query)) ||
        (workflow.created_by_email &&
          workflow.created_by_email.toLowerCase().includes(query))
      );
    });

    // Then sort the filtered results
    return [...filteredWorkflows].sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;

      switch (sortField) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'created_at':
          aValue = new Date(a.created_at).getTime();
          bValue = new Date(b.created_at).getTime();
          break;
        case 'updated_at':
          aValue = new Date(a.updated_at).getTime();
          bValue = new Date(b.updated_at).getTime();
          break;
        case 'created_by':
          aValue = (a.created_by_email || '').toLowerCase();
          bValue = (b.created_by_email || '').toLowerCase();
          break;
        case 'last_run_at':
          aValue = a.last_run_at ? new Date(a.last_run_at).getTime() : 0;
          bValue = b.last_run_at ? new Date(b.last_run_at).getTime() : 0;
          break;
        case 'total_runs':
          aValue = a.total_runs ?? 0;
          bValue = b.total_runs ?? 0;
          break;
        case 'latest_run_cost_usd': {
          const aNull = a.latest_run_cost_usd == null;
          const bNull = b.latest_run_cost_usd == null;
          if (aNull && bNull) return 0;
          if (aNull) return 1;
          if (bNull) return -1;
          aValue = a.latest_run_cost_usd!;
          bValue = b.latest_run_cost_usd!;
          break;
        }
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [
    savedWorkflows,
    sortField,
    sortDirection,
    searchQuery,
    selectedLabels,
    showMyWorkflows,
    showSharedWithMe,
    user?.email,
  ]);

  // Calculate pagination
  const totalPages = useMemo(
    () => Math.ceil(filteredAndSortedWorkflows.length / ITEMS_PER_PAGE),
    [filteredAndSortedWorkflows.length],
  );

  // Get paginated workflows
  const paginatedWorkflows = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return filteredAndSortedWorkflows.slice(startIndex, endIndex);
  }, [filteredAndSortedWorkflows, currentPage]);

  const renderSortIcon = (columnField: WorkflowSortField) => {
    if (sortField !== columnField) {
      return <ChevronsUpDown className='size-4 text-gray-400' />;
    }
    if (sortDirection === 'asc') {
      return <ChevronUp className='size-4' />;
    }
    return <ChevronDown className='size-4' />;
  };

  const getAriaSort = (
    columnField: WorkflowSortField,
  ): 'ascending' | 'descending' | 'none' => {
    if (sortField !== columnField) {
      return 'none';
    }
    return sortDirection === 'asc' ? 'ascending' : 'descending';
  };

  const columns: ColumnDef<WorkflowDetails, unknown>[] = [
    {
      accessorKey: 'name',
      meta: { className: 'whitespace-normal py-3' },
      header: () => (
        <button
          type='button'
          className='flex items-center gap-1 hover:text-gray-900 dark:hover:text-white transition-colors'
          onClick={() => onSort?.('name')}
          aria-label='Sort by title'
          aria-sort={getAriaSort('name')}
        >
          Title
          {renderSortIcon('name')}
        </button>
      ),
      cell: ({ row }) => {
        const workflow = row.original;
        const role = workflow.user_role;
        const canEditLabels =
          role === 'workflow:owner' || role === 'workflow:editor';
        return (
          <WorkflowTitleCell
            workflow={workflow}
            orgId={currentOrg?.orgid}
            isLabelsEnabled={isLabelsEnabled}
            canEditLabels={canEditLabels}
            viewerOnly={isViewerOnly(workflow)}
          />
        );
      },
    },
    {
      accessorKey: 'createdBy',
      header: () => (
        <button
          type='button'
          className='flex items-center gap-1 hover:text-gray-900 dark:hover:text-white transition-colors'
          onClick={() => onSort?.('created_by')}
          aria-label='Sort by creator'
          aria-sort={getAriaSort('created_by')}
        >
          Creator
          {renderSortIcon('created_by')}
        </button>
      ),
      cell: ({ row }) => {
        const workflow = row.original;
        const viewerOnly = isViewerOnly(workflow);
        const email = workflow.created_by_email || '';
        const username = email.split('@')[0];

        if (!isCreatorEmailTruncated) {
          return (
            <div
              className={cn(
                'text-sm max-w-[200px] dark:text-white truncate',
                viewerOnly && 'opacity-50',
              )}
            >
              {email}
            </div>
          );
        }

        return (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type='button'
                className={cn(
                  'group/copy flex items-center gap-1 text-sm dark:text-white truncate max-w-[140px] cursor-pointer',
                  viewerOnly && 'opacity-50',
                )}
                onClick={async (e) => {
                  e.stopPropagation();
                  try {
                    await navigator.clipboard.writeText(email);
                    notification.success('Email copied');
                  } catch {
                    notification.error('Failed to copy');
                  }
                }}
              >
                <span className='truncate'>{username}</span>
                <Copy className='size-3 shrink-0 opacity-0 group-hover/copy:opacity-50 transition-opacity' />
              </button>
            </TooltipTrigger>
            <TooltipContent>
              <p>
                {email}
                <br />
                Click to copy
              </p>
            </TooltipContent>
          </Tooltip>
        );
      },
      size: 100,
    },
    {
      accessorKey: 'updatedAt',
      header: () => (
        <button
          type='button'
          className='flex items-center gap-1 hover:text-gray-900 dark:hover:text-white transition-colors'
          onClick={() => onSort?.('updated_at')}
          aria-label='Sort by last modified'
          aria-sort={getAriaSort('updated_at')}
        >
          Last Modified
          {renderSortIcon('updated_at')}
        </button>
      ),
      cell: ({ row }) => {
        const workflow = row.original;
        const viewerOnly = isViewerOnly(workflow);
        return (
          <div
            className={cn(
              'flex items-center gap-2 text-sm dark:text-white',
              viewerOnly && 'opacity-50',
            )}
          >
            {formatTimeAgo(workflow.updated_at)}
          </div>
        );
      },
      size: 100,
    },
    {
      accessorKey: 'createdAt',
      header: () => (
        <button
          type='button'
          className='flex items-center gap-1 hover:text-gray-900 dark:hover:text-white transition-colors'
          onClick={() => onSort?.('created_at')}
          aria-label='Sort by date created'
          aria-sort={getAriaSort('created_at')}
        >
          Date Created
          {renderSortIcon('created_at')}
        </button>
      ),
      cell: ({ row }) => {
        const workflow = row.original;
        const viewerOnly = isViewerOnly(workflow);
        return (
          <div
            className={cn(
              'flex items-center gap-2 text-sm dark:text-white',
              viewerOnly && 'opacity-50',
            )}
          >
            {safeFormatDate(new Date(workflow.created_at), 'MMMM d, yyyy')}
          </div>
        );
      },
      size: 100,
    },
    {
      accessorKey: 'lastRunAt',
      header: () => (
        <button
          type='button'
          className='flex items-center gap-1 hover:text-gray-900 dark:hover:text-white transition-colors'
          onClick={() => onSort?.('last_run_at')}
          aria-label='Sort by last run'
          aria-sort={getAriaSort('last_run_at')}
        >
          Last Run
          {renderSortIcon('last_run_at')}
        </button>
      ),
      cell: ({ row }) => {
        const workflow = row.original;
        const viewerOnly = isViewerOnly(workflow);
        return (
          <div
            className={cn(
              'text-sm dark:text-white',
              viewerOnly && 'opacity-50',
            )}
            title={
              workflow.last_run_at
                ? new Date(workflow.last_run_at).toLocaleString()
                : undefined
            }
          >
            {workflow.last_run_at ? formatTimeAgo(workflow.last_run_at) : '—'}
          </div>
        );
      },
      size: 100,
    },
    {
      id: 'a2aExposed',
      accessorKey: 'publish_to_api',
      header: () => (
        <div
          className='flex items-center gap-1'
          title='Exposed via the A2A protocol. Toggle on the workflow editor to flip publish_to_api.'
        >
          API
        </div>
      ),
      cell: ({ row }) => {
        const workflow = row.original;
        if (!workflow.publish_to_api) {
          return <div className='text-xs text-muted-foreground'>—</div>;
        }
        return (
          <div
            className='inline-flex items-center gap-1 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 px-2 py-0.5 text-[10px] font-medium'
            title='This workflow is exposed via the A2A protocol (publish_to_api=true) — allowlisted agents can run it via list_workflows / call_workflow.'
          >
            <span className='size-1.5 rounded-full bg-emerald-500' />
            A2A
          </div>
        );
      },
      size: 60,
      enableSorting: false,
    },
    {
      accessorKey: 'totalRuns',
      header: () => (
        <button
          type='button'
          className='flex items-center gap-1 hover:text-gray-900 dark:hover:text-white transition-colors'
          onClick={() => onSort?.('total_runs')}
          aria-label='Sort by total runs'
          aria-sort={getAriaSort('total_runs')}
        >
          Total Runs
          {renderSortIcon('total_runs')}
        </button>
      ),
      cell: ({ row }) => {
        const workflow = row.original;
        const viewerOnly = isViewerOnly(workflow);
        return (
          <div
            className={cn(
              'text-sm dark:text-white',
              viewerOnly && 'opacity-50',
            )}
          >
            {workflow.total_runs ?? 0}
          </div>
        );
      },
      size: 80,
    },
    ...(isWorkflowRunCostEnabled
      ? [
          {
            accessorKey: 'latestRunCost',
            header: () => (
              <button
                type='button'
                className='flex items-center gap-1 hover:text-gray-900 dark:hover:text-white transition-colors'
                onClick={() => onSort?.('latest_run_cost_usd')}
                aria-label='Sort by latest run cost'
                aria-sort={getAriaSort('latest_run_cost_usd')}
              >
                Latest Run Cost
                {renderSortIcon('latest_run_cost_usd')}
              </button>
            ),
            cell: ({ row }: { row: { original: WorkflowDetails } }) => {
              const workflow = row.original;
              const viewerOnly = isViewerOnly(workflow);
              // Distinguish "couldn't fetch the cost" from "the workflow never
              // ran". Both arrive as a null cost from the backend; total_runs
              // disambiguates.
              const costLabel =
                formatRunCost(workflow.latest_run_cost_usd) ??
                ((workflow.total_runs ?? 0) === 0 ? 'N/A' : 'Unavailable');
              return (
                <div
                  className={cn(
                    'text-sm dark:text-white',
                    viewerOnly && 'opacity-50',
                  )}
                >
                  {costLabel}
                </div>
              );
            },
            size: 100,
          },
        ]
      : []),
    ...(isWorkflowSchedulingEnabled
      ? [
          {
            id: 'schedule',
            header: () => <span>Schedule</span>,
            cell: ({ row }: { row: { original: WorkflowDetails } }) => {
              const workflow = row.original;
              const viewerOnly = isViewerOnly(workflow);
              if (!workflow.has_schedule || !workflow.schedule_cron) {
                return (
                  <div
                    className={cn(
                      'text-sm dark:text-white',
                      viewerOnly && 'opacity-50',
                    )}
                  >
                    —
                  </div>
                );
              }
              const cronDesc = humanCron(
                parseCronToMode(workflow.schedule_cron),
              );
              const nextRun = workflow.schedule_next_run_at
                ? formatTimeUntil(workflow.schedule_next_run_at)
                : null;
              const paused = workflow.schedule_enabled === false;
              const extraCount = (workflow.schedule_count ?? 1) - 1;
              return (
                <div
                  className={cn(
                    'text-sm space-y-0.5',
                    viewerOnly && 'opacity-50',
                  )}
                >
                  <div className='flex items-center gap-1.5'>
                    <CalendarClock className='size-3 shrink-0 text-blue-500' />
                    <span className='dark:text-white truncate'>{cronDesc}</span>
                    {paused && (
                      <span className='text-[10px] px-1 py-0.5 rounded bg-yellow-500/20 text-yellow-600 dark:text-yellow-400 leading-none'>
                        Paused
                      </span>
                    )}
                    {extraCount > 0 && !viewerOnly && (
                      <button
                        type='button'
                        className='text-[10px] px-1.5 py-0.5 rounded-full bg-blue-500/20 text-blue-600 dark:text-blue-400 font-medium leading-none hover:bg-blue-500/30 transition-colors cursor-pointer'
                        onClick={(e) => {
                          e.stopPropagation();
                          setSchedulingWorkflowId(workflow.workflow_id);
                          setSchedulingWorkflowName(workflow.name ?? '');
                          setIsScheduleModalOpen(true);
                        }}
                      >
                        +{extraCount}
                      </button>
                    )}
                  </div>
                  {nextRun && !paused && (
                    <div className='text-xs text-muted-foreground'>
                      Next: {nextRun}
                    </div>
                  )}
                </div>
              );
            },
            size: 160,
          },
        ]
      : []),
    // {
    //   accessorKey: 'id',
    //   header: 'Workflow ID',
    //   cell: ({ row }) => {
    //     const id = row.original.workflow_id;
    //     return (
    //       <div className='flex items-center gap-2'>
    //         <span className='font-mono text-sm text-gray-600'>{id}</span>
    //         <CopyButton text={id || ''} size='small' />
    //       </div>
    //     );
    //   },
    // },
    {
      id: 'actions',
      header: '',
      meta: {
        className:
          'sticky right-0 z-10 min-w-12 bg-background overflow-visible [tr:hover_&]:bg-muted [tr[data-state=selected]_&]:bg-muted after:absolute after:top-0 after:bottom-0 after:-left-6 after:w-6 after:pointer-events-none after:shadow-none [[data-scroll=scrollable]_&]:after:shadow-[inset_-8px_0_8px_-8px_rgba(0,0,0,0.12)]',
      },
      cell: ({ row }) => {
        const workflow = row.original;

        if (isViewerOnly(workflow)) {
          const pending = hasPendingRequest(workflow);
          return (
            <div className='flex justify-end'>
              {pending ? (
                <span className='inline-flex items-center gap-1 text-xs text-muted-foreground'>
                  <Clock className='size-3' />
                  Pending
                </span>
              ) : (
                <button
                  type='button'
                  className='inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs text-muted-foreground hover:text-foreground hover:bg-muted transition-colors'
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRequestAccess(workflow.workflow_id);
                  }}
                >
                  Request Access
                </button>
              )}
            </div>
          );
        }

        const pendingCount = workflow.pending_request_count ?? 0;

        return (
          <div className='flex justify-end'>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant='ghost'
                  size='sm'
                  className='relative h-8 w-8 p-0'
                  aria-label={`Open options for ${workflow.name}`}
                  onClick={(event) => event.stopPropagation()}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.stopPropagation();
                    }
                  }}
                >
                  <MoreVertical className='h-4 w-4' />
                  {pendingCount > 0 && (
                    <span className='absolute -top-1 -right-1 flex size-4 items-center justify-center rounded-full bg-orange-500 text-[10px] font-medium text-white'>
                      {pendingCount}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align='end'
                onClick={(event) => event.stopPropagation()}
                onKeyDown={(event) => event.stopPropagation()}
              >
                {getWorkflowActions(workflow).map((action) => (
                  <Fragment key={action.label}>
                    {action.label === 'Delete' && <DropdownMenuSeparator />}
                    <DropdownMenuItem
                      className={action.className}
                      onSelect={action.onSelect}
                      disabled={action.disabled}
                    >
                      {action.icon}
                      {action.label}
                      {action.badge != null && (
                        <span className='ml-auto size-2 rounded-full bg-orange-500' />
                      )}
                    </DropdownMenuItem>
                  </Fragment>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    },
  ];

  const viewContent =
    viewMode === 'grid' ? (
      <div className='w-full'>
        {isLoading || isDuplicating ? (
          <div className='flex flex-col items-center justify-center py-16 px-4 min-h-[400px] w-full'>
            <div className='w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4'></div>
            <p className='text-gray-500'>
              {isDuplicating
                ? 'Duplicating workflow...'
                : 'Loading workflows...'}
            </p>
          </div>
        ) : savedWorkflows.length === 0 ? (
          <EmptyState openCreateModal={openCreateModal} />
        ) : (
          <>
            <div className='grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3'>
              {paginatedWorkflows.map((workflow) => {
                const viewerOnly = isViewerOnly(workflow);
                const clientData = hasClientDataLabel(workflow.labels);
                const collaborators = [
                  workflow.created_by_email || workflow.created_by_name,
                  ...(workflow.collaborators ?? []).map(
                    (entry) => entry.email || entry.name,
                  ),
                ].filter((value): value is string => Boolean(value));
                const overflowMenu = viewerOnly ? (
                  hasPendingRequest(workflow) ? (
                    <span className='inline-flex h-8 items-center gap-1 px-2 text-xs text-muted-foreground'>
                      <Clock className='size-3' />
                      Pending
                    </span>
                  ) : (
                    <Button
                      variant='ghost'
                      size='sm'
                      className='h-8 text-xs'
                      onClick={(event) => {
                        event.stopPropagation();
                        handleRequestAccess(workflow.workflow_id);
                      }}
                    >
                      Request Access
                    </Button>
                  )
                ) : (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant='ghost'
                        size='icon'
                        className='relative size-8'
                        aria-label={`Open options for ${workflow.name}`}
                        onClick={(event) => event.stopPropagation()}
                        onKeyDown={(event) => event.stopPropagation()}
                      >
                        <MoreHorizontal className='size-4' />
                        {(workflow.pending_request_count ?? 0) > 0 && (
                          <span className='absolute -right-1 -top-1 flex size-4 items-center justify-center rounded-full bg-orange-500 text-[10px] font-medium text-white'>
                            {workflow.pending_request_count}
                          </span>
                        )}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align='end'
                      onClick={(event) => event.stopPropagation()}
                      onKeyDown={(event) => event.stopPropagation()}
                    >
                      {getWorkflowActions(workflow).map((action) => (
                        <Fragment key={action.label}>
                          {action.label === 'Delete' && (
                            <DropdownMenuSeparator />
                          )}
                          <DropdownMenuItem
                            className={action.className}
                            onSelect={action.onSelect}
                            disabled={action.disabled}
                          >
                            {action.icon}
                            {action.label}
                            {action.badge != null && (
                              <span className='ml-auto flex size-5 items-center justify-center rounded-full bg-orange-500 text-[11px] font-medium text-white'>
                                {action.badge}
                              </span>
                            )}
                          </DropdownMenuItem>
                        </Fragment>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                );
                return (
                  <WorkflowCard
                    key={workflow.workflow_id}
                    layout='stacked'
                    name={workflow.name}
                    description={workflow.description}
                    isPublic={isSharedPublicly(workflow)}
                    nodeCount={workflow.node_count}
                    lastRunAt={workflow.last_run_at}
                    clientData={clientData}
                    collaborators={[...new Set(collaborators)]}
                    overflowMenu={overflowMenu}
                    onOpen={
                      viewerOnly
                        ? undefined
                        : () =>
                            navigate(
                              `${ROUTES.WORKFLOW.ROOT}/${workflow.workflow_id}`,
                            )
                    }
                    className={cn('h-full', viewerOnly && 'opacity-75')}
                  />
                );
              })}
            </div>
            {totalPages > 1 && (
              <div className='mt-4'>
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handleSetCurrentPage}
                />
              </div>
            )}
          </>
        )}
      </div>
    ) : (
      <div className='w-full'>
        {isLoading || isDuplicating ? (
          <div className='flex flex-col items-center justify-center py-16 px-4 min-h-[400px] w-full'>
            <div className='w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4'></div>
            <p className='text-gray-500'>
              {isDuplicating
                ? 'Duplicating workflow...'
                : 'Loading workflows...'}
            </p>
          </div>
        ) : savedWorkflows.length === 0 ? (
          <EmptyState openCreateModal={openCreateModal} />
        ) : (
          <>
            <Table
              columns={columns}
              data={paginatedWorkflows}
              loading={false}
              onRowClick={(workflow, event) => {
                if (!isViewerOnly(workflow)) {
                  if (event.ctrlKey || event.metaKey) {
                    window.open(
                      orgPath(
                        `${ROUTES.WORKFLOW.ROOT}/${workflow.workflow_id}`,
                      ),
                      '_blank',
                    );
                  } else {
                    navigate(`${ROUTES.WORKFLOW.ROOT}/${workflow.workflow_id}`);
                  }
                }
              }}
              rowClassName={(workflow) =>
                cn('group/row', isViewerOnly(workflow) ? '' : 'cursor-pointer')
              }
            />
            {totalPages > 1 && (
              <div className='mt-4'>
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handleSetCurrentPage}
                />
              </div>
            )}
          </>
        )}
      </div>
    );

  return (
    <>
      {viewContent}

      {/* Edit Workflow Modal */}
      <SaveWorkflowModal
        isModalOpen={isEditModalOpen}
        setIsModalOpen={(isOpen) => {
          setIsEditModalOpen(isOpen);
          if (!isOpen) {
            // Drop the lazily-loaded payload so the next open re-fetches
            // and we don't briefly show a stale ``publish_to_api`` state.
            setEditLoadedWorkflow(null);
          }
        }}
        isExistingWorkflow={true}
        currentWorkflowName={editingWorkflow?.name || ''}
        currentWorkflowDescription={editingWorkflow?.description || ''}
        workflowId={editingWorkflow?.workflow_id}
        onSave={handleUpdateWorkflow}
        mode='editInfo'
        currentDefaultRole={
          (editingWorkflow?.default_role as DefaultRoleOption) ?? null
        }
        showClientDataWarning={isShareClientDataWarningEnabled}
        isWorkflowLocked={!!editingWorkflow?.is_locked}
        // undefined keeps the toggle hidden -- fetch in flight, viewer, or a
        // failed load -- so the modal degrades gracefully.
        currentPublishToApi={
          editingWorkflow &&
          editLoadedWorkflow &&
          (editingWorkflow.user_role === 'workflow:owner' ||
            (user?.email &&
              editingWorkflow.created_by_email?.toLowerCase() ===
                user.email.toLowerCase()) ||
            isWorkflowAdmin)
            ? Boolean(editLoadedWorkflow.publish_to_api)
            : undefined
        }
        onPublishToApiChange={async (next) => {
          if (!editingWorkflow || !editLoadedWorkflow || !currentOrg?.orgid) {
            return;
          }
          const prev = editLoadedWorkflow.publish_to_api ?? false;
          // Optimistically flip both the loaded payload (for the modal)
          // and the list row (for the API badge column) -- mirrors the
          // canvas's optimistic store flip in ``setPublishToApi``.
          const optimisticLoaded: Workflow = {
            ...editLoadedWorkflow,
            publish_to_api: next,
          };
          setEditLoadedWorkflow(optimisticLoaded);
          setSavedWorkflows(
            savedWorkflows.map((w) =>
              w.workflow_id === editingWorkflow.workflow_id
                ? { ...w, publish_to_api: next }
                : w,
            ),
          );
          try {
            const resp = await updateWorkflow(
              optimisticLoaded,
              currentOrg.orgid,
            );
            if (resp?.detail) throw new Error(resp.detail);
          } catch (err) {
            setEditLoadedWorkflow({
              ...editLoadedWorkflow,
              publish_to_api: prev,
            });
            setSavedWorkflows(
              savedWorkflows.map((w) =>
                w.workflow_id === editingWorkflow.workflow_id
                  ? { ...w, publish_to_api: prev }
                  : w,
              ),
            );
            Sentry.captureException(err, {
              extra: {
                context: 'workflow-list-edit-publish-to-api',
                workflowId: editingWorkflow.workflow_id,
                next,
              },
            });
            notification.error('Failed to update API exposure');
          }
        }}
        onDefaultRoleChange={async (role) => {
          if (!currentOrg?.orgid || !editingWorkflow?.workflow_id) return;
          try {
            await setWorkflowDefaultRole(
              currentOrg.orgid,
              editingWorkflow.workflow_id,
              role,
            );
            const updated = savedWorkflows.map((w) =>
              w.workflow_id === editingWorkflow.workflow_id
                ? { ...w, default_role: role }
                : w,
            );
            setSavedWorkflows(updated);
            setEditingWorkflow({
              ...editingWorkflow,
              default_role: role,
            });
            notification.success('Default access updated');
          } catch (err) {
            Sentry.captureException(err, {
              extra: {
                context: 'edit-modal-default-role',
                workflowId: editingWorkflow.workflow_id,
                role,
              },
            });
            notification.error('Failed to update default access');
          }
        }}
      />

      {signOffWorkflow && (
        <SignOffModal
          isOpen={isSignOffModalOpen}
          onClose={() => setIsSignOffModalOpen(false)}
          orgId={currentOrg?.orgid}
          workflowId={signOffWorkflow.workflow_id}
          workflowName={signOffWorkflow.name ?? ''}
        />
      )}

      {applyUatWorkflow && (
        <ApplyToUatModal
          isOpen={isApplyUatModalOpen}
          onClose={() => setIsApplyUatModalOpen(false)}
          orgId={currentOrg?.orgid}
          workflowId={applyUatWorkflow.workflow_id}
          workflowName={applyUatWorkflow.name ?? ''}
        />
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        title='Delete Workflow'
        description={`Are you sure you want to delete "${workflowToDelete?.name}"? All artifacts created from this workflow will be deleted as well. Shared users will no longer be able to view or download them.`}
        buttonLabel='Delete'
        isLoading={isDeleting}
        handleDelete={confirmDeleteWorkflow}
        handleCancel={cancelDeleteWorkflow}
      />

      {/* Schedule Modal */}
      {schedulingWorkflowId && (
        <Modal
          isDialogOpen={isScheduleModalOpen}
          setIsDialogOpen={(open) => {
            setIsScheduleModalOpen(open);
            if (!open) {
              setSchedulingWorkflowId(null);
              if (scheduleChangedRef.current && currentOrg?.orgid) {
                getWorkflowList(currentOrg.orgid);
              }
              scheduleChangedRef.current = false;
            }
          }}
          title={
            <span className='flex items-center gap-2'>
              <CalendarClock className='size-4' />
              Schedule
            </span>
          }
          contentClassName='max-w-md'
        >
          <ScheduleConfigSection
            variant='modal'
            workflowId={schedulingWorkflowId}
            workflowName={schedulingWorkflowName}
            isVisible={isScheduleModalOpen}
            onChanged={() => {
              scheduleChangedRef.current = true;
            }}
          />
        </Modal>
      )}

      {/* Share Modal */}
      {sharingWorkflowId && (
        <ShareModal
          resourceType='workflow'
          resourceId={sharingWorkflowId}
          orgId={currentOrg?.orgid ?? null}
          open={isShareModalOpen}
          onOpenChange={(open) => {
            setIsShareModalOpen(open);
            if (!open) setSharingWorkflowId(null);
          }}
          defaultRole={
            (savedWorkflows.find((w) => w.workflow_id === sharingWorkflowId)
              ?.default_role as 'workflow:viewer' | 'workflow:editor' | null) ??
            null
          }
          onRequestReviewed={() => {
            if (!sharingWorkflowId) return;
            const updated = savedWorkflows.map((w) =>
              w.workflow_id === sharingWorkflowId
                ? {
                    ...w,
                    pending_request_count: Math.max(
                      0,
                      (w.pending_request_count ?? 1) - 1,
                    ),
                  }
                : w,
            );
            setSavedWorkflows(updated);
          }}
          onDefaultRoleChange={(role) => {
            if (!sharingWorkflowId) return;
            const updated = savedWorkflows.map((w) =>
              w.workflow_id === sharingWorkflowId
                ? { ...w, default_role: role }
                : w,
            );
            setSavedWorkflows(updated);
          }}
          showClientDataWarning={isShareClientDataWarningEnabled}
        />
      )}

      {/* Create-orchestrator modal: opens from the kebab "Create orchestrator"
          item, walks publish_to_api -> auto-describe -> create-agent, then
          surfaces the once-only bearer + A2A URLs. */}
      <CreateOrchestratorModal
        isOpen={isOrchestratorModalOpen}
        orgId={currentOrg?.orgid ?? ''}
        workflow={orchestratorWorkflow}
        onClose={() => {
          setIsOrchestratorModalOpen(false);
          setOrchestratorWorkflow(null);
        }}
        onCreated={() => {
          // The workflow's publish_to_api flag was likely flipped during the
          // create flow; reflect that in the list optimistically so the
          // "API" badge updates without a round-trip.
          if (!orchestratorWorkflow) return;
          const wfId = orchestratorWorkflow.workflow_id;
          setSavedWorkflows(
            savedWorkflows.map((w) =>
              w.workflow_id === wfId ? { ...w, publish_to_api: true } : w,
            ),
          );
        }}
      />
    </>
  );
};
